import { Fragment } from 'react';

import type { PortPort } from '../Port/Port';
import type { SlotChipTier, SlotFace, SlotOrientation } from '../Slots/Slots';
import type { ChassisLayout, ChassisPort, LayoutColumn, LayoutSection } from './types';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { styled } from '../../stitches.config';
import { ChassisGap } from '../Chassis/atoms/ChassisGap';
import { ChassisGroup } from '../Chassis/atoms/ChassisGroup';
import { ChassisRear } from '../Chassis/atoms/ChassisRear';
import { ChassisRow } from '../Chassis/atoms/ChassisRow';
import { ChassisSection } from '../Chassis/atoms/ChassisSection';
import { Port } from '../Port/Port';
import { getPortIndicators } from '../Port/utils';
import { Slot } from '../Slots/Slots';

/**
 * A neutral box for the disabled tooltip to hang off.
 *
 * `Tooltip` merges its trigger's props into whatever element it wraps, and one of them is an
 * `onClick`. Given straight to the glyph, that reads as "this port is clickable" and the glyph makes
 * itself a button — inside the slot that already is one, which is the second tab stop this file
 * strips `onClick` to avoid. The box takes the merge instead: a `div` with a handler on it is
 * neither focusable nor a widget.
 */
const PortTooltipTarget = styled('div', {
  display: 'flex',
});
PortTooltipTarget.displayName = 'PortTooltipTarget';

const columnKey = (column: LayoutColumn) =>
  `${column.type}-${column.rows.map((row) => row.ports.join(',')).join('|')}`;

const sectionKey = (section: LayoutSection) =>
  `${section.group ?? ''}-${section.columns.map(columnKey).join('|')}`;

/**
 * Which lane each port's connection chip hangs in, by port number. See `SlotChip`.
 *
 * Counted across every column rather than per column, so the alternation survives a group boundary —
 * a 12px `trailingGap` is not room for two full-width names side by side. Every declared slot counts
 * whether or not it's patched, so a chip's lane doesn't move when a neighbour is plugged in.
 */
function slotChipTiers(columns: LayoutColumn[]): Map<number, SlotChipTier> {
  const tiers = new Map<number, SlotChipTier>();
  const counts: Record<SlotOrientation, number> = { top: 0, bottom: 0 };

  for (const column of columns) {
    for (const row of column.rows) {
      for (const portNumber of row.ports) {
        const count = counts[row.orientation];
        counts[row.orientation] = count + 1;
        tiers.set(portNumber, count % 2 === 0 ? 'near' : 'far');
      }
    }
  }

  return tiers;
}

type ChassisColumnsProps = {
  columns: LayoutColumn[];
  /** Which side of the chassis these columns draw on. `rear` for anything inside a `ChassisRear`. */
  face?: SlotFace;
  portLabels?: Record<number, string>;
  ports: ChassisPort[];
};

function ChassisColumns({ columns, face, portLabels, ports }: ChassisColumnsProps) {
  const tiers = slotChipTiers(columns);

  return columns.map((column, columnIndex) => (
    <Fragment key={`column-${columnKey(column)}`}>
      <ChassisGroup label={column.group} icon={column.groupIcon}>
        {column.rows.map((row) => (
          <ChassisRow key={`row-${column.type}-${row.ports.join('-')}-${row.orientation}`}>
            {row.ports.map((portNumber) => {
              // A port the caller said nothing about still gets drawn — as an empty socket.
              const port: Partial<ChassisPort> =
                ports.find((candidate) => candidate.number === portNumber) ?? {};
              // The slot owns the click target; a second one on the glyph would double the focus.
              // `poe` and `spanMirroring` belong to the indicators alone — `Port` would put
              // them on the DOM.
              const { connection, muted, onClick, poe, spanMirroring, tooltip, ...glyph } = port;

              // Named field by field rather than spread, so a new `PortProps` flag can't
              // silently start meaning something to the rail.
              const indicators = getPortIndicators({
                blocking: glyph.blocking,
                isL3Port: glyph.isL3Port,
                isOspfEnabled: glyph.isOspfEnabled,
                poe,
                spanMirroring,
                uplink: glyph.uplink,
              });

              const diagram = (
                <Port
                  {...glyph}
                  number={portNumber}
                  port={column.type}
                  orientation={row.portOrientation}
                />
              );
              const contents = tooltip ?? (glyph.status === 'disabled' ? 'Disabled' : null);

              return (
                <Slot
                  key={`slot-${column.type}-${portNumber}`}
                  chipSpeed={glyph.negotiatedPortSpeed}
                  chipStatus={glyph.status}
                  chipTier={tiers.get(portNumber)}
                  connection={connection}
                  diagram={
                    contents ? (
                      <Tooltip contents={contents}>
                        <PortTooltipTarget>{diagram}</PortTooltipTarget>
                      </Tooltip>
                    ) : (
                      diagram
                    )
                  }
                  face={face}
                  indicators={indicators}
                  label={portLabels?.[portNumber]}
                  muted={muted}
                  number={portNumber}
                  onClick={onClick}
                  orientation={row.orientation}
                  selected={glyph.selected}
                />
              );
            })}
          </ChassisRow>
        ))}
      </ChassisGroup>
      {column.trailingGap && columnIndex < columns.length - 1 && (
        <ChassisGap width={column.trailingGap} />
      )}
    </Fragment>
  ));
}

export type ChassisPortsProps = {
  /** Where this unit's ports sit on its faceplate. */
  layout: ChassisLayout;
  /** The unit's ports, in any order — the layout decides where each number lands. */
  ports: ChassisPort[];
};

/**
 * Draws one unit's faceplate from a model layout: a `ChassisSection` per section, a `ChassisGroup`
 * per column, a `ChassisRow` per row, a `Slot` per port. Meant to sit inside a `ChassisUnit`.
 *
 * Numbers in the layout are the unit's own port numbers, so an HA pair is two units each numbered
 * from 1 rather than one unit with an offset.
 */
export function ChassisPorts({ layout, ports }: ChassisPortsProps) {
  return (
    <>
      {layout.sections.map((section, sectionIndex) => (
        <Fragment key={`section-${sectionKey(section)}`}>
          <ChassisSection label={section.group} icon={section.groupIcon}>
            <ChassisColumns
              columns={section.columns}
              portLabels={section.portLabels}
              ports={ports}
            />
          </ChassisSection>
          {section.trailingGap && sectionIndex < layout.sections.length - 1 && (
            <ChassisGap width={section.trailingGap} />
          )}
        </Fragment>
      ))}
      {layout.sections.map(
        (section) =>
          section.rearColumns && (
            <ChassisRear key={`rear-${sectionKey(section)}`}>
              <ChassisColumns
                columns={section.rearColumns}
                face="rear"
                portLabels={section.portLabels}
                ports={ports}
              />
            </ChassisRear>
          ),
      )}
    </>
  );
}

export type ChassisRearPanelPort = {
  /** Distinguishes this entry's column from the others. The owning unit's id does. */
  id: string;
  /** The owning unit's ports. Each entry brings its own, so two units may both number theirs 0. */
  ports: ChassisPort[];
  /** Which of those ports the panel draws. */
  number: number;
  /** Connector. */
  type: PortPort;
  /** Number-chip text, since these ports are named rather than numbered ('OOB'). */
  label?: string;
};

export type ChassisRearPanelProps = {
  /** One column per port, in faceplate order. */
  ports: ChassisRearPanelPort[];
};

/**
 * A rear panel holding one port from each of several units.
 *
 * A unit whose rear ports are its own declares them as its layout's `rearColumns`. A pair that
 * gathers both halves' management ports into one panel at the far edge of the card can't — the
 * panel outlives either layout — so it passes them here instead, each with the ports of the unit it
 * came from. Render it as the last child of the trailing `ChassisUnit`.
 */
export function ChassisRearPanel({ ports }: ChassisRearPanelProps) {
  return (
    <ChassisRear>
      {ports.map((port) => (
        <ChassisColumns
          key={`rear-panel-${port.id}`}
          face="rear"
          columns={[
            {
              type: port.type,
              rows: [
                // Empty row pads the column so the port sits on the unit's bottom edge, as it does
                // when the panel comes from a layout's own `rearColumns`.
                { ports: [], orientation: 'bottom', portOrientation: 'up' },
                { ports: [port.number], orientation: 'top', portOrientation: 'up' },
              ],
            },
          ]}
          portLabels={port.label !== undefined ? { [port.number]: port.label } : undefined}
          ports={port.ports}
        />
      ))}
    </ChassisRear>
  );
}
